import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { HttpService } from '../../core/services/http.service';
import { WellsApiService } from '../../services/wells-api.service';
import { WellViewMappingService } from '../../services/wellview-mapping.service';
import { NotificationService } from '../../services/notification.service';
import { ConfirmationService } from '../../services/confirmation.service';
import { LoadingService } from '../../services/loading.service';
import { UnitConversionService } from '../../services/unit-conversion.service';

@Component({
    selector: 'review-changes',
    templateUrl: './review-changes.component.html',
    styleUrls: ['./review-changes.component.scss'],
    standalone: false
})
export class ReviewChangesComponent implements OnInit, OnChanges {
    @Input() wellName: string = '';
    @Input() schemaType: string = '';
    @Input() displayKeys: string[] = [];
    @Input() changedRecords: any[] = [];
    @Input() wellIda: string = '';  // API Number
    @Input() bu: string = '';  // Business Unit (RBU or ABU)
    // No global isLoading for review-changes; only table-specific loaders
    
    // Use shared operationId for all unit conversions in review component
    private readonly REVIEW_OPERATION_ID = 'review';
    
    // Wellbore idrec data retrieved from localStorage
    private selectedIdRecData: any = null;
    private isInitialized: boolean = false;

    // Table-specific loading states for skeleton loader on navigation
    public currentDataLoading: boolean = false;
    public wellViewDataLoading: boolean = false;
    public currentDataError: string = '';
    public wellViewDataError: string = '';
    public isSubmitting: boolean = false;

    @Output() back = new EventEmitter<void>();
    @Output() submit = new EventEmitter<void>();

    public showWellViewData: boolean = false;
    public displayRecords: any[] = [];
    public currentData: any[] = [];
    public wellViewData: any[] = [];
    public currentDataIndex: number = 0;
    public wellViewDataIndex: number = 0;
    public wellViewSchemaKey: string = ''; // Store the WellView schema key (e.g., 'wvWellHeader')
    public fieldsToUpdate: string[] = []; // Fields from prepare response
    public fieldsToUpdateContent: any = null; // Content object with updated values from prepare response
    public prepareResponse: any = null; // Store prepare response for reference

    constructor(
        private httpService: HttpService,
        private wellsApiService: WellsApiService,
        private router: Router,
        private wellViewMappingService: WellViewMappingService,
        private notificationService: NotificationService,
        private confirmationService: ConfirmationService,
        private loadingService: LoadingService,
        public unitConversionService: UnitConversionService
    ) {}

    ngOnInit() {
        // Retrieve selected idrec data from localStorage
        const storedIdRec = localStorage.getItem('selectedIdRec');
        if (storedIdRec) {
            try {
                this.selectedIdRecData = JSON.parse(storedIdRec);
            } catch (e) {
                console.error('Error parsing selectedIdRec from localStorage:', e);
            }
        }
        
        // Load mappings first, then load data
        this.wellViewMappingService.getMappings().subscribe({
            next: () => {
                this.isInitialized = true;
                this.loadData();
            },
            error: (err) => {
                console.error('Error loading WellView mappings:', err);
                // Still load data even if mappings fail
                this.isInitialized = true;
                this.loadData();
            }
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        // Only load data if component is initialized (prevents duplicate call during ngOnInit)
        if (this.isInitialized && (changes['wellName'] || changes['schemaType'])) {
            this.loadData();
        }
    }

    private loadData() {
        if (!this.wellName || !this.schemaType) {
            console.warn('Missing wellName or schemaType, skipping load');
            return;
        }
        
        this.currentDataLoading = true;
        this.wellViewDataLoading = true;
        this.currentDataError = '';
        this.wellViewDataError = '';
        this.currentData = [];
        this.wellViewData = [];

        // Get WellView schema name using the mapping service
        this.wellViewSchemaKey = this.wellViewMappingService.getWellViewSchemaName(this.schemaType);

        this.wellsApiService.getSchema(this.wellName, this.schemaType, true).subscribe({
            next: (data: any) => {
                this.currentData = data || [];
                this.currentDataLoading = false;
                this.currentDataIndex = 0;
            },
            error: (err: any) => {
                console.error('Error loading current data:', err);
                this.currentDataError = 'Failed to load current data.';
                this.currentDataLoading = false;
                this.notificationService.error('Failed to load current data: ' + (err.error?.detail || err.message || 'Unknown error'));
            }
        });
        
        this.wellsApiService.getWellViewData(
            this.wellViewSchemaKey, 
            this.wellName, 
            this.wellIda, 
            this.bu,
            this.selectedIdRecData?.idrec
        ).subscribe({
            next: (data: any) => {
                this.wellViewData = data && data.records ? data.records : [];
                this.wellViewDataLoading = false;
                this.wellViewDataIndex = 0;
                this.prepareDisplayRecords();
            },
            error: (err: any) => {
                console.error('Error loading wellview data:', err);
                this.wellViewDataError = 'No record found in the Wellview for selected schema.';
                this.wellViewDataLoading = false;
                this.notificationService.error('No record found in the Wellview for selected schema: ' + this.schemaType);
                this.prepareDisplayRecords();
            }
        });
    }

    /**
     * Process prepare response to extract fields_to_update
     * @param prepareResponse - The response from the prepare API endpoint
     */
    public processPrepareResponse(prepareResponse: any): void {
        if (!prepareResponse || !prepareResponse.operations || prepareResponse.operations.length === 0) {
            return;
        }

        this.prepareResponse = prepareResponse;
        
        // Process all operations to extract fields_to_update
        for (const operation of prepareResponse.operations) {
            if (operation.metadata) {
                // Extract fields_to_update from operation metadata
                if (operation.metadata.fields_to_update && Array.isArray(operation.metadata.fields_to_update)) {
                    this.fieldsToUpdate = operation.metadata.fields_to_update;
                }
                
                // Extract content object which contains the values to be updated
                if (operation.content) {
                    this.fieldsToUpdateContent = operation.content;
                }
            }
        }
    }



    prepareDisplayRecords() {
        if (this.changedRecords && this.changedRecords.length > 0) {
            this.displayRecords = this.changedRecords;
        } else if (this.wellViewData && this.wellViewData.length > 0) {
            // If no changes, show all records as unmodified
            this.displayRecords = this.wellViewData.map((record, index) => ({
                rowNumber: index + 1,
                original: record,
                modified: record,
                changes: {} // Empty changes object indicates no edits
            }));
        } else {
            this.displayRecords = [];
        }
    }
    
    // Current Data Carousel Navigation
    selectCurrentDataRecord(index: number): void {
        if (index >= 0 && index < this.currentData.length) {
            this.triggerCurrentDataLoading(() => {
                this.currentDataIndex = index;
            });
        }
    }

    nextCurrentDataRecord(): void {
        if (this.currentDataIndex < this.currentData.length - 1) {
            this.triggerCurrentDataLoading(() => {
                this.currentDataIndex++;
            });
        }
    }

    prevCurrentDataRecord(): void {
        if (this.currentDataIndex > 0) {
            this.triggerCurrentDataLoading(() => {
                this.currentDataIndex--;
            });
        }
    }

    private triggerCurrentDataLoading(callback: () => void) {
        this.currentDataLoading = true;
        setTimeout(() => {
            callback();
            this.currentDataLoading = false;
        }, 500); // 500ms skeleton loader
    }
    
    // Well View Data Carousel Navigation
    selectWellViewRecord(index: number): void {
        if (index >= 0 && index < this.wellViewData.length) {
            this.triggerWellViewDataLoading(() => {
                this.wellViewDataIndex = index;
            });
        }
    }

    nextWellViewRecord(): void {
        if (this.wellViewDataIndex < this.wellViewData.length - 1) {
            this.triggerWellViewDataLoading(() => {
                this.wellViewDataIndex++;
            });
        }
    }

    prevWellViewRecord(): void {
        if (this.wellViewDataIndex > 0) {
            this.triggerWellViewDataLoading(() => {
                this.wellViewDataIndex--;
            });
        }
    }

    private triggerWellViewDataLoading(callback: () => void) {
        this.wellViewDataLoading = true;
        setTimeout(() => {
            callback();
            this.wellViewDataLoading = false;
        }, 500); // 500ms skeleton loader
    }

    
    // Helper to check if a field is modified
    isFieldModified(recordIndex: number, key: string): boolean {
        // Implementation depends on data structure
        return false; 
    }

    toggleWellViewData() {
        this.showWellViewData = !this.showWellViewData;
    }

    onSubmit() {
        if (!this.wellIda || !this.bu) {
            console.error('Missing wellIda or bu for prepare API call');
            this.notificationService.error('Missing required information (Well ID or Business Unit) for prepare API call');
            this.submit.emit();
            return;
        }
        
        // Show confirmation dialog before starting prepare
        this.confirmationService.show({
            title: 'Prepare Data for Approval',
            message: 'Are you sure you want to prepare this data for approval?',
            warningText: 'This action will prepare data to be written to WellView.',
            confirmText: 'Prepare',
            cancelText: 'Cancel',
            type: 'info',
            confirmButtonType: 'save',
            confirmCallback: () => {
                this.executePrepare();
            },
            cancelCallback: () => {
                // Prepare cancelled by user
            }
        });
    }

    private executePrepare() {
        this.isSubmitting = true;
        
        // Show loading overlay immediately (before API call)
        this.loadingService.show('Preparing data for approval...');
        
        // Determine if we should use well header or schema-specific prepare
        const normalizedSchema = this.schemaType.toLowerCase();
        let prepareObservable: any;
        
        if (normalizedSchema === 'wellheader') {
            prepareObservable = this.wellsApiService.prepareWellHeaderChanges(this.wellIda, this.bu);
        } else {
            // Prepare idrec parameter - only required for specific schemas
            let idrec: string | undefined;
            
            // Schemas that require idrec parameter - COMPREHENSIVE list with all variations
            const schemasRequiringIdrec = [
                // Wellbore
                'Wellbore', 'wellbore',
                // Wellbore Section/Size
                'WellboreSz', 'wellbore_sz', 'wellboresz', 'wellbore-sz', 'wellbore sz',
                'WellboreSection', 'wellbore_section', 'wellboresection', 'wellbore-section', 'wellbore section',
                // Casing
                'Cas', 'cas', 'casing', 'Casing',
                // Cement
                'Cement', 'cement',
                // Geological Horizon/Formation
                'WellboreFormation', 'wellboreformation', 'wellbore_formation', 'wellbore-formation', 'wellbore formation',
                'GeologicalHorizon', 'geologicalhorizon', 'geological_horizon', 'geological-horizon', 'geological horizon',
                'geo_horizon', 'geohorizon', 'geo-horizon', 'geo horizon',
                // Perforation
                'Perforation', 'perforation',
                // Job
                'Job', 'job', 'jobs', 'Jobs',
                // Other in Hole
                'OtherInHole', 'otherinhole', 'other_in_hole', 'other-in-hole', 'other in hole', 'other inhole',
                // Tubing
                'Tubing', 'tubing',
                // Rod
                'Rod', 'rod', 'rod_details', 'roddetails', 'rod-details', 'rod details', 'RodDetails',
                // Component schemas
                'CasComp', 'cascomp', 'casing_component', 'casingcomponent', 'casing-component', 'casing component',
                'casing_components', 'casingcomponents', 'casing-components', 'casing components',
                'CasingComponent', 'CasingComponents',
                'CementStage', 'cementstage', 'cement_stage', 'cement-stage', 'cement stage',
                'TubingComp', 'tubingcomp', 'tubing_comp', 'tubing-comp', 'tubing comp',
                'tubing_components', 'tubingcomponents', 'tubing-components', 'tubing components',
                'TubingComponents',
                'RodComp', 'rodcomp', 'rod_comp', 'rod-comp', 'rod comp',
                'rod_components', 'rodcomponents', 'rod-components', 'rod components',
                'RodComponents'
            ];
            const normalizedSchemaName = this.schemaType;
            
            // Check if this schema requires idrec
            if (schemasRequiringIdrec.includes(normalizedSchemaName)) {
                // idrec is required for this schema
                if (!this.selectedIdRecData || !this.selectedIdRecData.idrec) {
                    console.error('Missing idrec for schema:', this.schemaType);
                    this.notificationService.error('Missing wellbore idrec. Please select a wellbore from the wells list.');
                    this.isSubmitting = false;
                    this.loadingService.hide();
                    return;
                }
                idrec = this.selectedIdRecData.idrec;
            }
            
            // Pass schema name directly - prepareSchemaChanges will do the endpoint mapping
            prepareObservable = this.wellsApiService.prepareSchemaChanges(
                this.schemaType, 
                this.wellIda, 
                this.bu,
                idrec
            );
        }
        
        prepareObservable.subscribe({
            next: (response: any) => {
                // Process the prepare response to extract fields_to_update and detect unit variants
                this.processPrepareResponse(response);
                this.isSubmitting = false;
                this.loadingService.hide();
                this.notificationService.success('Data prepared successfully for approval');
                // Navigate to approval page with the response data
                this.router.navigate(['/wells', this.wellName, 'review', 'approve'], {
                    state: { prepareData: response, schemaType: this.schemaType }
                });
            },
            error: (err: any) => {
                console.error('Prepare API ERROR:', err);
                this.isSubmitting = false;
                this.loadingService.hide();
                this.notificationService.error('Something went wrong. Please try again after sometime.');
            }
        });
    }

    onBack() {
        // Only emit event - let parent component handle navigation and data reloading
        this.back.emit();
    }
    
    getDisplayLabel(key: string): string {
        if (!key) return '';
        
        // If the key is from fieldsToUpdate (prepare response), use it as-is
        if (this.fieldsToUpdate && this.fieldsToUpdate.includes(key)) {
            return key;
        }
        
        // If the key contains spaces, slashes, or is already a label format, return as-is
        // This handles current data API which returns "API Number", "Operator Name", etc.
        if (/[\s\/\-]/.test(key)) {
            return key;
        }
        
        // Special mapping for Wellbore schema WellView fields
        const wellboreFieldMap: { [key: string]: string } = {
            'des': 'Wellbore Name',
            'wellboreida': 'Wellbore IDA',
            'WellboreIDA': 'Wellbore IDA',
            'Des': 'Wellbore Name',
            'DES': 'Wellbore Name'
        };
        
        if (wellboreFieldMap[key]) {
            return wellboreFieldMap[key];
        }
        
        // Use WellViewMappingService to get the label from wv_mappings.json
        // This works for wellview data which has field names like "WellIDA", "Operator"
        if (this.wellViewSchemaKey) {
            const label = this.wellViewMappingService.getFieldLabelSync(this.wellViewSchemaKey, key);
            return label;
        }
        
        // Fallback formatting if schema key is not available
        return key
            .replace(/_/g, ' ')
            .replace(/([A-Z])/g, ' $1')
            .toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
            .trim();
    }
    
    formatValue(key: string, value: any): string {
        // Handle null, undefined, and empty string
        if (value === null || value === undefined || value === '') {
            return '-';
        }
        
        // Format date fields to show only date without time
        const dateFields = ['dttmend', 'dttmstart', 'start_date', 'end_date', 'start date', 'end date'];
        if (dateFields.includes(key.toLowerCase())) {
            try {
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                    // Format as YYYY-MM-DD
                    return date.toISOString().split('T')[0];
                }
            } catch (e) {
                // If parsing fails, return '-' for invalid dates
                return '-';
            }
        }
        
        // Convert to string and trim, then show '-' if empty
        const stringValue = String(value).trim();
        return stringValue === '' ? '-' : stringValue;
    }
    
    /**
     * Get filtered display keys for Current Data table, excluding unit variant fields and converted_values
     */
    getFilteredDisplayKeys(): string[] {
        if (!this.displayKeys || this.displayKeys.length === 0) {
            return [];
        }
        
        // Filter out unit variant keys and converted_values so they don't show as separate columns
        return this.displayKeys.filter(key => !this.isUnitVariantKey(key));
    }

    getWellViewDisplayKeys(): string[] {
        if (!this.wellViewData || this.wellViewData.length === 0) {
            return [];
        }
        
        // Collect all unique keys from all records (not just first one)
        const allKeys = new Set<string>();
        this.wellViewData.forEach(record => {
            Object.keys(record).forEach(key => allKeys.add(key));
        });
        
        // Get all available keys from the wellview data, excluding internal fields, converted_values, and unit variants
        const availableKeys = Array.from(allKeys).filter(key => {
            const lowerKey = key.toLowerCase();
            return !lowerKey.startsWith('sys') && 
                   lowerKey !== 'idrec' && 
                   lowerKey !== 'idrecparent' && 
                   lowerKey !== 'idrecparenttk' &&
                   lowerKey !== 'idwell' &&
                   lowerKey !== 'converted_values' &&
                   !this.isUnitVariantKey(key);
        });
        
        // Use WellViewMappingService to get ordered keys based on wv_mappings.json
        if (this.wellViewSchemaKey) {
            const orderedKeys = this.wellViewMappingService.getOrderedDisplayKeysSync(
                this.wellViewSchemaKey, 
                availableKeys
            );
            
            // If we got ordered keys, use them; otherwise return available keys
            return orderedKeys.length > 0 ? orderedKeys : availableKeys;
        }
        
        // Fallback: return available keys without ordering
        return availableKeys;
    }
    
    /**
     * Check if a key is a unit variant key or internal field that shouldn't be displayed
     */
    private isUnitVariantKey(key: string): boolean {
        return this.unitConversionService.isUnitVariantKey(key);
    }
    
    /**
     * Normalize a field name for comparison (remove spaces, convert to lowercase)
     * Helper method for field name mapping
     */
    private normalizeFieldName(name: string): string {
        return name.toLowerCase().replace(/[\s_]+/g, '');
    }
    
    /**
     * Check if a display field has unit variants
     */
    public hasUnitVariants(key: string): boolean {
        if (!key) return false;
        
        // Check across all data sources to ensure we catch unit variants
        let hasVariants = false;
        
        if (this.currentData && this.currentData.length > 0) {
            hasVariants = this.unitConversionService.hasUnitVariants(key, this.currentData[0]);
        }
        
        if (!hasVariants && this.wellViewData && this.wellViewData.length > 0) {
            hasVariants = this.unitConversionService.hasUnitVariants(key, this.wellViewData[0]);
        }
        
        return hasVariants;
    }
    
    /**
     * Get the content value for a field from the prepare response
     * Intelligently maps display field names to content object keys
     * @param fieldName - Display name of the field (e.g., "Start Depth")
     * @returns The value from the content object, or null if not found
     */
    public getContentValueForField(fieldName: string): any {
        if (!this.fieldsToUpdateContent || !fieldName) {
            return null;
        }

        // Try exact match first
        if (this.fieldsToUpdateContent.hasOwnProperty(fieldName)) {
            return this.fieldsToUpdateContent[fieldName];
        }

        // Try to find by normalizing the field name
        // Examples: "Start Depth" -> "depthstart", "Top Depth" -> "depthtop", etc.
        const normalizedFieldName = this.normalizeFieldName(fieldName);
        for (const key in this.fieldsToUpdateContent) {
            if (this.normalizeFieldName(key) === normalizedFieldName) {
                return this.fieldsToUpdateContent[key];
            }
        }

        // Try common field name mappings if direct match fails
        const fieldMappings: { [display: string]: string[] } = {
            'Top Depth': ['depthtop', 'topDepth', 'top_depth'],
            'Bottom Depth': ['depthbtm', 'bottomDepth', 'bottom_depth', 'depthbottom'],
            'Start Depth': ['depthstart', 'startDepth', 'start_depth'],
            'Total Depth': ['depthtotal', 'totalDepth', 'total_depth'],
            'Start Date & Time': ['dttmstart', 'startDateTime', 'start_date_time'],
            'End Date & Time': ['dttmend', 'endDateTime', 'end_date_time'],
            'Proposed/Actual': ['proposedoractual', 'proposedOrActual', 'proposed_or_actual']
        };

        const mappings = fieldMappings[fieldName];
        if (mappings) {
            for (const mappedKey of mappings) {
                if (this.fieldsToUpdateContent.hasOwnProperty(mappedKey)) {
                    return this.fieldsToUpdateContent[mappedKey];
                }
            }
        }

        return null;
    }

    /**
     * Get prepared content data object which contains all field values to update
     * Public method for template binding
     */
    public getPreparedContentData(): any {
        if (!this.fieldsToUpdateContent) {
            return null;
        }

        // Create a formatted object with display names and their values
        const contentData: any = {};
        if (this.fieldsToUpdate && Array.isArray(this.fieldsToUpdate)) {
            for (const fieldName of this.fieldsToUpdate) {
                contentData[fieldName] = this.getContentValueForField(fieldName);
            }
        }
        return contentData;
    }

    /**
     * Get available units for a specific field
     */
    public getAvailableUnitsForField(key: string): string[] {
        if (!key) return [];
        
        // Check across all data sources to get all available units
        let units: string[] = [];
        
        if (this.currentData && this.currentData.length > 0) {
            units = this.unitConversionService.getAvailableUnits(key, this.currentData[0]);
        }
        
        if (units.length === 0 && this.wellViewData && this.wellViewData.length > 0) {
            units = this.unitConversionService.getAvailableUnits(key, this.wellViewData[0]);
        }
        
        return units;
    }
    
    /**
     * Get the selected unit for a specific field
     */
    public getSelectedUnitForField(fieldName: string): string {
        if (!fieldName) return '';
        
        // Get reference data for unit detection
        const referenceData = this.currentData?.[0] || this.wellViewData?.[0];
        if (!referenceData) return '';
        
        return this.unitConversionService.getSelectedUnit(this.REVIEW_OPERATION_ID, fieldName, referenceData);
    }
    
    /**
     * Get the value for the selected unit for a specific field
     */
    public getValueForUnit(record: any, baseKey: string): any {
        if (!record || !baseKey) {
            return '';
        }
        
        return this.unitConversionService.getValueForUnit(this.REVIEW_OPERATION_ID, baseKey, record);
    }
    
    /**
     * Set selected unit for a specific field
     */
    public setSelectedUnit(fieldName: string, unit: string): void {
        if (!fieldName || !unit) return;
        
        this.unitConversionService.setSelectedUnit(this.REVIEW_OPERATION_ID, fieldName, unit);
    }
    
    /**
     * Get display label for a unit
     */
    public getUnitDisplayLabel(unit: string): string {
        return this.unitConversionService.getUnitDisplayLabel(unit);
    }
}
